import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Loader2, Mail, ShieldCheck, Copy, Check } from "lucide-react";
import { adminAuthHeader } from "../../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight admin-invite modal. Posts to the existing
 * /api/admin/auth/invite endpoint and surfaces the accept_url so the
 * inviter can copy + send it via their own channel if Resend is down.
 */
export const InviteAdminModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const [submitting, setSubmitting] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/.+@.+\..+/.test(email)) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/auth/invite`,
        { email: email.trim().toLowerCase(), role },
        { headers: adminAuthHeader() },
      );
      setAcceptUrl(data?.accept_url || null);
      toast.success(`Invite sent to ${email.trim()}.`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn't send the invite. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!acceptUrl) return;
    try {
      await navigator.clipboard.writeText(acceptUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Invite link copied.");
    } catch {
      toast.error("Couldn't copy. Highlight and copy manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="invite-admin-modal"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--light-border)]">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-[var(--bone-soft)] text-[var(--charcoal)] flex items-center justify-center">
              <ShieldCheck size={18} strokeWidth={2.25} />
            </span>
            <div>
              <h2 className="font-['Outfit'] text-lg font-bold text-[var(--charcoal)] leading-tight">
                Invite an admin
              </h2>
              <p className="text-[12px] text-[var(--text-muted)]">
                They'll get an email with a one-time link valid for 7 days.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--bone-soft)] text-[var(--text-muted)]"
            data-testid="invite-admin-close"
          >
            <X size={18} />
          </button>
        </div>

        {!acceptUrl ? (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--charcoal)] mb-1.5">
                Email
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@medicysltd.co.uk"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-sm focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20"
                  data-testid="invite-admin-email"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[var(--charcoal)] mb-1.5">
                Role
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-sm focus:outline-none focus:border-[var(--teal)] focus:ring-2 focus:ring-[var(--teal)]/20"
                data-testid="invite-admin-role"
              >
                <option value="admin">Admin (full access)</option>
                <option value="viewer">Viewer (read-only)</option>
              </select>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-[var(--charcoal)] hover:bg-[var(--bone-soft)]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--lime)] text-[var(--dark-bg)] text-sm font-bold hover:bg-[var(--lime-dark)] disabled:opacity-60"
                data-testid="invite-admin-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Send invite
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-4">
            <div className="rounded-xl bg-[var(--bone-soft)] border border-[var(--light-border)] p-4">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-1.5">
                Single-use accept link
              </p>
              <p
                className="text-[12px] font-mono text-[var(--charcoal)] break-all leading-snug"
                data-testid="invite-admin-accept-url"
              >
                {acceptUrl}
              </p>
            </div>
            <p className="text-xs text-[var(--text-muted)] leading-relaxed">
              The invitee will receive this link by email. If they don't see
              it within a minute, you can copy and send it directly.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-sm font-semibold hover:bg-[var(--bone-soft)]"
                data-testid="invite-admin-copy"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--charcoal)] text-white text-sm font-semibold hover:bg-[var(--dark-bg)]"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteAdminModal;
